import { useCallback, useEffect, useState } from 'react';
import { Mail, Phone, Plus, X } from 'lucide-react';
import { api } from '../../../../services/api';
import { useWorkspace } from '../../WorkspaceContext';

const ROLE_OPTIONS = [
  { id: 'owner', label: 'Owner' },
  { id: 'manager', label: 'Manager' },
  { id: 'stylist', label: 'Stylist' },
  { id: 'therapist', label: 'Therapist' },
  { id: 'technician', label: 'Technician' },
  { id: 'receptionist', label: 'Receptionist' },
  { id: 'other', label: 'Other' },
];

const roleLabel = (role) => ROLE_OPTIONS.find((option) => option.id === role)?.label || 'Team member';

const emptyStaff = { name: '', title: '', role: 'stylist', email: '', phone: '' };

const ManageStaff = () => {
  const { activeLocation } = useWorkspace();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyStaff);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadStaff = useCallback(async () => {
    if (!activeLocation) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const result = await api.getWorkspaceStaff(activeLocation.id);
    if (result.error) {
      setError(result.error);
      setStaff([]);
    } else {
      setError('');
      setStaff(Array.isArray(result.data) ? result.data : []);
    }
    setLoading(false);
  }, [activeLocation]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyStaff);
    setFormOpen(true);
  };

  const openEdit = (member) => {
    setEditingId(member.id);
    setForm({
      name: member.name || '',
      title: member.title || '',
      role: member.role || 'other',
      email: member.email || '',
      phone: member.phone || '',
    });
    setFormOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      name: form.name.trim(),
      title: form.title.trim() || null,
      role: form.role,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      locationId: activeLocation?.id,
    };

    const result = editingId
      ? await api.updateWorkspaceStaff(editingId, payload)
      : await api.createWorkspaceStaff(payload);

    setSaving(false);
    if (result.error) {
      setError(result.error);
      return;
    }

    setFormOpen(false);
    await loadStaff();
  };

  const removeStaff = async (member) => {
    const result = await api.deleteWorkspaceStaff(member.id);
    if (result.error) {
      setError(result.error);
      return;
    }
    await loadStaff();
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-light text-ink-900 sm:text-3xl">Staff</h2>
          <p className="mt-1 text-sm text-ink-700/80">
            Your team at{' '}
            <span className="font-medium text-ink-900">{activeLocation?.name || 'this location'}</span>.
            Clients can pick a specific person when booking.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex flex-none items-center justify-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
        >
          <Plus className="h-4 w-4" />
          Add staff
        </button>
      </div>

      {error && (
        <div className="mt-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-6 bg-sand-50 ring-1 ring-ink-900/10">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
          </div>
        ) : staff.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="font-display text-2xl font-light text-ink-900">No staff yet</p>
            <p className="mt-2 text-sm text-ink-700/70">
              Add your team so clients can book with a specific person.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-ink-900/10">
            {staff.map((member) => (
              <li
                key={member.id}
                className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-ink-900 text-sm font-semibold text-saffron-400">
                    {(member.name || '?')[0].toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink-900">
                      {member.name}
                      <span className="ml-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-600/60">
                        {member.title || roleLabel(member.role)}
                      </span>
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-700/75">
                      {member.email && (
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5" />
                          {member.email}
                        </span>
                      )}
                      {member.phone && (
                        <span className="inline-flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5" />
                          {member.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-none gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(member)}
                    className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white"
                  >
                    Edit
                  </button>
                  {member.role !== 'owner' && (
                    <button
                      type="button"
                      onClick={() => removeStaff(member)}
                      className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-red-700 ring-1 ring-red-200 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-ink-950/50 p-3 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full max-w-md overflow-y-auto bg-sand-50 p-5 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10 sm:p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-light text-ink-900">
                {editingId ? 'Edit staff' : 'Add staff'}
              </h2>
              <button type="button" onClick={() => setFormOpen(false)} aria-label="Close">
                <X className="h-5 w-5 text-ink-700" />
              </button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSave}>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Full name
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="Amara Okonkwo"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Role
                  </label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Title
                  </label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="Senior stylist"
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Phone
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add staff'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStaff;
